import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Group, CreateExpenseData, Expense } from '../types';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { offlineService } from '../services/offlineService';
import { useToast } from '../hooks/useToast';
import { useAppContext } from '../contexts/AppContext';
import { haptics } from '../utils/haptics';
import { useKeyboardScroll, useAutoFocus } from '../hooks/useKeyboardScroll';
import AddExpenseChat from '../components/AddExpenseChat';
import styles from './AddExpense.module.css';
import { logger } from '../utils/logger';

interface AddExpenseProps {
  user: User;
  isModal?: boolean;
  onClose?: () => void;
  forceGroupId?: string; // Optional: force a specific group (e.g., Personal)
}

const CATEGORIES = [
  { icon: '🍔', name: 'Food', value: 'Food' },
  { icon: '🚗', name: 'Transport', value: 'Transport' },
  { icon: '🎬', name: 'Fun', value: 'Entertainment' },
  { icon: '🛍️', name: 'Shopping', value: 'Shopping' },
  { icon: '💡', name: 'Utilities', value: 'Utilities' },
  { icon: '✈️', name: 'Travel', value: 'Travel' },
  { icon: '🏥', name: 'Health', value: 'Healthcare' },
  { icon: '📚', name: 'Education', value: 'Education' },
];

const QUICK_AMOUNTS = [5, 10, 25, 50, 100];

const QUICK_SUGGESTIONS = [
  'Coffee $5',
  'Lunch $15',
  'Uber $12',
  'Movie $20'
];

// Helper function to get currency symbol
const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    RUB: '₽',
    KZT: '₸'
  };
  return symbols[currency] || currency;
};

const AddExpense: React.FC<AddExpenseProps> = ({ user, isModal = false, onClose, forceGroupId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { fetchGroups, invalidateExpenses, invalidateBalances } = useAppContext();
  
  // Check if we're editing an existing expense
  const editExpense = (location.state as any)?.editExpense as Expense | undefined;
  const isEditMode = !!editExpense;
  
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [splitMode, setSplitMode] = useState<'equal' | 'custom'>('equal');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [paidById, setPaidById] = useState<string>('');
  const [smartInput, setSmartInput] = useState('');
  const [parsingAI, setParsingAI] = useState(false);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  
  // Refs for keyboard handling
  const smartInputRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  
  // Auto-focus smart input on mount
  useAutoFocus(smartInputRef, true);
  
  // Handle keyboard scroll for both inputs
  useKeyboardScroll(smartInputRef);
  useKeyboardScroll(amountInputRef);
  const [expense, setExpense] = useState<CreateExpenseData>({
    title: '',
    description: '',
    amount: 0,
    currency: user.preferredCurrency || 'USD',
    category: 'Food',
    groupId: '',
    shares: []
  });

  const loadGroups = useCallback(async () => {
    try {
      let groupsData = await fetchGroups();
      
      // If forceGroupId is provided (e.g., Personal group), fetch it separately
      if (forceGroupId) {
        try {
          // Try to fetch Personal group first
          const personalGroup = await apiService.getPersonalGroup();
          if (personalGroup && personalGroup.id === forceGroupId) {
            groupsData = [personalGroup]; // Only show the Personal group
          } else {
            // Fallback: fetch specific group by ID
            const forcedGroup = await apiService.getGroup(forceGroupId);
            if (forcedGroup) {
              groupsData = [forcedGroup];
            }
          }
        } catch (error) {
          logger.error('Failed to fetch forced group:', error);
        }
      }
      
      setGroups(groupsData);
      
      if (groupsData.length > 0) {
        const savedGroupId = localStorage.getItem('selectedGroupId');
        const targetGroup = forceGroupId
          ? groupsData.find(g => g.id === forceGroupId) || groupsData[0]
          : savedGroupId 
            ? groupsData.find(g => g.id === savedGroupId) || groupsData[0]
            : groupsData[0];
        
        setPaidById(user.id);
        const memberIds = new Set(targetGroup.members.map(m => m.user.id));
        setSelectedMembers(memberIds);
        
        // For Personal groups, use user's preferred currency; for other groups, use group currency
        const currency = targetGroup.name === 'My Expenses' 
          ? (user.preferredCurrency || targetGroup.currency || 'USD')
          : (targetGroup.currency || 'USD');
        
        setExpense(prev => ({ 
          ...prev, 
          groupId: targetGroup.id,
          currency: currency
        }));
      }
    } catch (error) {
      logger.error('Failed to load groups:', error);
      addToast({
        type: 'error',
        message: t('toast.failedToLoadGroups'),
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [user.id, user.preferredCurrency, addToast, fetchGroups, t, forceGroupId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Pre-populate form if editing
  useEffect(() => {
    if (isEditMode && editExpense && groups.length > 0) {
      const amount = typeof editExpense.amount === 'number' ? editExpense.amount : parseFloat(editExpense.amount as any) || 0;
      
      setExpense({
        title: editExpense.title,
        description: editExpense.description,
        amount: amount,
        currency: editExpense.currency,
        category: editExpense.category || 'Food',
        groupId: editExpense.groupId,
        shares: editExpense.shares.map(s => ({
          userId: s.user?.id || s.userId,
          amount: typeof s.amount === 'number' ? s.amount : parseFloat(s.amount as any) || 0,
          percentage: s.percentage
        }))
      });
      
      setPaidById(editExpense.paidById);
      setSelectedMembers(new Set(editExpense.shares.map(s => s.user?.id || s.userId)));
      setSmartInput(`${editExpense.description || editExpense.title} $${amount.toFixed(2)}`);
    }
  }, [isEditMode, editExpense, groups]);

  // Update shares when amount, selected members, or split mode changes
  useEffect(() => {
    const selectedGroup = groups.find(g => g.id === expense.groupId);
    if (!selectedGroup) return;

    // For Personal expenses, always set shares to just the user with full amount
    if (selectedGroup.name === 'My Expenses') {
      if (expense.amount) {
        setExpense(prev => ({ 
          ...prev, 
          shares: [{ userId: user.id, amount: expense.amount }] 
        }));
      }
      return;
    }

    // For group expenses, check if members are selected
    if (!expense.amount || selectedMembers.size === 0) {
      setExpense(prev => ({ ...prev, shares: [] }));
      return;
    }

    if (splitMode === 'equal') {
      const equalAmount = expense.amount / selectedMembers.size;
      const shares = Array.from(selectedMembers).map(userId => ({
        userId,
        amount: parseFloat(equalAmount.toFixed(2))
      }));
      setExpense(prev => ({ ...prev, shares }));
    }
  }, [expense.amount, selectedMembers, splitMode, expense.groupId, groups, user.id]);

  const handleSmartParse = async () => {
    if (!smartInput.trim()) return;

    // Dismiss keyboard by blurring the active input
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setParsingAI(true);
    try {
      const result = await apiService.parseExpenseText(smartInput);
      
      if (result?.parsed) {
        const parsed = result.parsed;
        setExpense(prev => ({
          ...prev,
          title: parsed.title || smartInput,
          amount: parsed.amount || prev.amount,
          description: parsed.description || '',
          category: parsed.category || prev.category,
          currency: prev.currency // ✅ Preserve currency (user preference or group currency)
        }));

        addToast({
          type: 'success',
          message: `✨ ${t('toast.parsedSuccess')}`,
          duration: 2000
        });

        // Scroll to show the expense details/confirmation section
        setTimeout(() => {
          const amountSection = document.querySelector(`.${styles.amountSection}`);
          if (amountSection) {
            amountSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    } catch (error: unknown) {
      logger.error('AI Parse error:', error);
      // Fall back to using the input as title
      setExpense(prev => ({
        ...prev,
        title: smartInput,
        currency: prev.currency // ✅ Preserve currency even in error case
      }));
    } finally {
      setParsingAI(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!expense.groupId || expense.shares.length === 0 || !paidById) {
      addToast({
        type: 'error',
        message: t('toast.fillRequired'),
        duration: 3000
      });
      return;
    }

    // Validate shares sum to total
    const totalShares = expense.shares.reduce((sum, share) => sum + share.amount, 0);
    const difference = Math.abs(totalShares - expense.amount);
    if (difference > 0.01) {
      addToast({
        type: 'error',
        message: t('toast.sharesDoNotMatch', { difference: `$${difference.toFixed(2)}` }),
        duration: 3000
      });
      return;
    }

    setSubmitting(true);
    try {
      const expenseData = {
        ...expense,
        paidById
      };

      const selectedGroup = groups.find(g => g.id === expense.groupId);

      if (isEditMode && editExpense) {
        // Update existing expense (requires online)
        if (!navigator.onLine) {
          addToast({
            type: 'error',
            message: '📵 Cannot update expenses while offline',
            duration: 4000
          });
          setSubmitting(false);
          return;
        }

        // ✅ Only send fields that can be updated (backend expects title, description, amount, category)
        const updateData = {
          title: expenseData.title,
          description: expenseData.description || '',
          amount: expenseData.amount,
          category: expenseData.category
        };

        logger.debug('🔧 Updating expense:', { id: editExpense.id, updateData });
        
        await apiService.updateExpense(editExpense.id, updateData);
        
        logger.debug('✅ Expense updated successfully');
        
        invalidateExpenses(expense.groupId);
        invalidateBalances(expense.groupId);
        
        // 🎮 Haptic feedback: Expense Updated
        haptics.expenseAdded(); // Reusing same haptic for now
        
          addToast({
          type: 'success',
          message: `✅ ${t('toast.expenseUpdated', { group: selectedGroup?.name || 'group' })}`,
          duration: 3000
        });
      } else {
        // Create new expense (supports offline)
        if (navigator.onLine) {
          // Online: Create immediately
          await apiService.createExpense(expenseData);
          
          invalidateExpenses(expense.groupId);
          invalidateBalances(expense.groupId);
          
          // 🎮 Haptic feedback: Expense Added
          haptics.expenseAdded();
          
            addToast({
            type: 'success',
            message: `✅ ${t('toast.expenseAdded', { group: selectedGroup?.name || 'group' })}`,
            duration: 3000
          });
        } else {
          // Offline: Queue for sync
          const token = authService.getStoredToken();
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
          
          if (!token) {
            throw new Error('No auth token available');
          }

          await offlineService.queueExpense(expenseData, token, apiUrl);
          await offlineService.registerBackgroundSync();
          
          // Dispatch event to update queue count
          window.dispatchEvent(new Event('offline-expense-added'));
          
          // 🎮 Haptic feedback: Expense Queued
          haptics.expenseAdded();
          
          addToast({
            type: 'info',
            message: `📵 Expense queued - Will sync when online`,
            duration: 5000
          });
          
          logger.info('✅ Expense queued for offline sync');
        }
      }
      
      setTimeout(() => {
        if (isModal && onClose) {
          onClose();
        } else {
          navigate('/');
        }
      }, 500);
    } catch (error: unknown) {
      logger.error(`Failed to ${isEditMode ? 'update' : 'create'} expense:`, error);
      
      // Show more detailed error message
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || (error instanceof Error ? error.message : String(error)) || (isEditMode ? 'Failed to update expense' : 'Failed to create expense');
      
      addToast({
        type: 'error',
        message: `❌ ${errorMessage}`,
        duration: 5000
      });
      setSubmitting(false);
    }
  };

  const handleQuickAmount = (amount: number) => {
    setExpense(prev => ({ ...prev, amount }));
  };

  const handleMemberToggle = (userId: string) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSmartInput(suggestion);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className={styles.errorContainer}>
        <h2>No Groups Available</h2>
        <p>You need to be part of a group to add expenses.</p>
        <button 
          className={styles.submitButton}
          onClick={() => navigate('/groups')}
        >
          Create a Group
        </button>
      </div>
    );
  }

  // Use chat-style interface for new expenses (not editing)
  if (!isEditMode) {
    return (
      <AddExpenseChat
        user={user}
        groups={groups}
        initialGroupId={forceGroupId || undefined}
        onExpenseCreated={(groupId: string) => {
          // Invalidate caches
          invalidateExpenses(groupId);
          invalidateBalances(groupId);
          
          // Close modal or navigate back
          if (isModal && onClose) {
            onClose();
          } else {
            navigate('/');
          }
        }}
        onCancel={() => {
          if (isModal && onClose) {
            onClose();
          } else {
            navigate(-1);
          }
        }}
      />
    );
  }

  const selectedGroup = groups.find(g => g.id === expense.groupId);

  // Fallback to old form for editing
  return (
    <div className={styles.addExpenseModern}>
      {/* Header */}
      {!isModal && (
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>{isEditMode ? t('addExpense.editTitle') : t('addExpense.title')}</h1>
          <p className={styles.headerSubtitle}>{t('addExpense.whatDidYouSpend')}</p>
        </div>
      )}

      {/* Active Group Indicator */}
      {selectedGroup && (
        <div className={styles.activeGroupBanner}>
          <div className={styles.groupIconCircle}>
            {selectedGroup.name?.charAt(0) || '?'}
          </div>
          <div className={styles.groupInfo}>
            <span className={styles.groupLabel}>{t('addExpense.addingTo')}:</span>
            <span className={styles.groupName}>
              {selectedGroup.name === 'My Expenses' ? t('dashboard.personal') : selectedGroup.name}
            </span>
          </div>
          {!forceGroupId && groups.length > 1 && (
            <button
              className={styles.changeGroupBtn}
              onClick={() => setShowGroupSelector(!showGroupSelector)}
            >
              {t('addExpense.changeGroup')}
            </button>
          )}
        </div>
      )}
      
      {/* Group Selector Dropdown */}
      {showGroupSelector && !forceGroupId && (
        <div className={styles.groupSelectorDropdown}>
          {groups.map(group => (
            <button
              key={group.id}
              className={`${styles.groupOption} ${expense.groupId === group.id ? styles.active : ''}`}
              onClick={() => {
                // For Personal groups, use user's preferred currency; for other groups, use group currency
                const currency = group.name === 'My Expenses'
                  ? (user.preferredCurrency || group.currency || 'USD')
                  : (group.currency || 'USD');
                
                setExpense(prev => ({ ...prev, groupId: group.id, currency: currency }));
                const memberIds = new Set(group.members.map(m => m.user.id));
                setSelectedMembers(memberIds);
                setShowGroupSelector(false);
              }}
            >
              <div className={styles.groupIcon}>{group.name?.charAt(0) || '?'}</div>
              <div className={styles.groupDetails}>
                <div className={styles.groupName}>{group.name}</div>
                <div className={styles.groupMeta}>{group.members?.length || 0} members</div>
              </div>
              {expense.groupId === group.id && <span className={styles.checkmark}>✓</span>}
            </button>
          ))}
        </div>
      )}

      {/* Smart Input Section */}
      <div className={styles.smartInputSection}>
        <div className={styles.smartInputLabel}>
          ✨ {t('addExpense.whatDidYouSpend')}
        </div>
        <div className={styles.smartInputWrapper}>
          <input
            ref={smartInputRef}
            type="text"
            className={styles.smartInput}
            value={smartInput}
            onChange={(e) => setSmartInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSmartParse();
              }
            }}
            placeholder={t('addExpense.placeholder')}
          />
          <button
            type="button"
            className={`${styles.parseButton} ${parsingAI ? styles.parsing : ''}`}
            onClick={handleSmartParse}
            disabled={!smartInput.trim() || parsingAI}
          >
            {parsingAI ? '⏳' : '✨'}
          </button>
        </div>
        <div className={styles.quickSuggestions}>
          {QUICK_SUGGESTIONS.map((suggestion, index) => (
            <div
              key={index}
              className={styles.suggestionChip}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
        
        {/* Voice Message Tip */}
        <div className={styles.voiceTip}>
          🎤 <strong>Pro tip:</strong> Send voice messages to @{process.env.REACT_APP_BOT_USERNAME || 'settlerx_bot'} in your group chat - AI will parse them automatically!
        </div>
      </div>

      {/* Amount Section */}
      <div className={styles.amountSection}>
        <div className={styles.amountHeader}>
          <span className={styles.amountLabel}>{t('addExpense.amount')}</span>
          <select
            className={styles.currencySelector}
            value={expense.currency}
            onChange={(e) => setExpense(prev => ({ ...prev, currency: e.target.value }))}
          >
            <option value="USD">USD $</option>
            <option value="EUR">EUR €</option>
            <option value="GBP">GBP £</option>
            <option value="RUB">RUB ₽</option>
            <option value="KZT">KZT ₸</option>
          </select>
        </div>
        <div className={styles.amountInputWrapper}>
          <div className={styles.currencySymbol}>{getCurrencySymbol(expense.currency)}</div>
          <input
            ref={amountInputRef}
            type="number"
            className={styles.amountInput}
            value={expense.amount || ''}
            onChange={(e) => setExpense(prev => ({ 
              ...prev, 
              amount: parseFloat(e.target.value) || 0 
            }))}
            placeholder="0"
            step="0.01"
            min="0"
            required
          />
        </div>
        <div className={styles.quickAmounts}>
          {QUICK_AMOUNTS.map(amount => (
            <button
              key={amount}
              type="button"
              className={styles.quickAmountButton}
              onClick={() => handleQuickAmount(amount)}
            >
              {getCurrencySymbol(expense.currency)}{amount}
            </button>
          ))}
        </div>
      </div>

      {/* Category Section - Visual Icon Grid */}
      <div className={styles.categorySection}>
        <div className={styles.categoryLabel}>{t('addExpense.category')}</div>
        <div className={styles.categoryGrid}>
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              type="button"
              className={`${styles.categoryButton} ${expense.category === cat.value ? styles.selected : ''}`}
              onClick={() => setExpense(prev => ({ ...prev, category: cat.value }))}
            >
              <div className={styles.categoryIcon}>{cat.icon}</div>
              <div className={styles.categoryName}>{t(`categories.${cat.value.toLowerCase()}`)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Description (Optional, Compact) */}
      <div className={styles.descriptionSection}>
        <div className={styles.descriptionLabel}>{t('addExpense.description')}</div>
        <textarea
          className={styles.descriptionInput}
          value={expense.description}
          onChange={(e) => setExpense(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('addExpense.description')}
          rows={2}
        />
      </div>

      {/* Split Section - Hide for Personal expenses */}
      {selectedGroup && selectedGroup.name !== 'Personal' && (
        <div className={styles.splitSection}>
          <div className={styles.splitLabel}>{t('addExpense.splitWith')}</div>
          
          {/* Split Method Selector */}
          <div className={styles.splitOptions}>
            <button
              type="button"
              className={`${styles.splitOption} ${splitMode === 'equal' ? styles.selected : ''}`}
              onClick={() => setSplitMode('equal')}
            >
              <div className={styles.splitOptionIcon}>⚖️</div>
              <div className={styles.splitOptionText}>
                <div className={styles.splitOptionTitle}>{t('addExpense.splitEqually')}</div>
                <div className={styles.splitOptionDesc}>{t('addExpense.splitEqually')}</div>
              </div>
            </button>
            <button
              type="button"
              className={`${styles.splitOption} ${splitMode === 'custom' ? styles.selected : ''}`}
              onClick={() => setSplitMode('custom')}
            >
              <div className={styles.splitOptionIcon}>✏️</div>
              <div className={styles.splitOptionText}>
                <div className={styles.splitOptionTitle}>{t('addExpense.customSplit')}</div>
                <div className={styles.splitOptionDesc}>{t('addExpense.customSplit')}</div>
              </div>
            </button>
          </div>

          {/* Participant Selection */}
          <div className={styles.participantSelection}>
            <div className={styles.participantSelectionTitle}>
              {t('addExpense.paidBy')}
            </div>
            <div className={styles.participantChips}>
              {selectedGroup.members.map(member => {
                const isPayer = paidById === member.user.id;
                const displayName = member.user.id === user.id ? 'You' : member.user.firstName;
                
                return (
                  <div
                    key={member.user.id}
                    className={`${styles.participantChip} ${isPayer ? styles.paidBy : ''}`}
                    onClick={() => setPaidById(member.user.id)}
                  >
                    {displayName} {isPayer && '💰'}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.participantSelection}>
            <div className={styles.participantSelectionTitle}>
              {t('addExpense.splitWith')}
            </div>
            <div className={styles.participantChips}>
              {selectedGroup.members.map(member => {
                const isSelected = selectedMembers.has(member.user.id);
                const displayName = member.user.id === user.id ? 'You' : member.user.firstName;
                const share = expense.shares.find(s => s.userId === member.user.id);
                const shareAmount = share?.amount || 0;
                
                return (
                  <div
                    key={member.user.id}
                    className={`${styles.participantChip} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleMemberToggle(member.user.id)}
                  >
                    {displayName} {isSelected && `(${getCurrencySymbol(expense.currency)}${shareAmount.toFixed(2)})`}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Submit Section */}
      <div className={styles.submitSection}>
        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={submitting || !expense.amount || !paidById || (selectedGroup?.name !== 'Personal' && selectedMembers.size === 0)}
        >
          <span className={styles.submitIcon}>✓</span>
          {submitting ? t('common.loading') : (isEditMode ? t('addExpense.update') : t('addExpense.submit'))}
        </button>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => isModal ? onClose?.() : navigate(-1)}
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};

export default AddExpense;
